import Dropzone, { type DropEvent } from 'react-dropzone'

import PublishIcon from '@mui/icons-material/Publish'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useSnackbar } from 'notistack'

function bytesToMB(bytes: number): string {
    const megabytes = bytes / (1024 * 1024)
    return megabytes.toFixed(2)
}

type Props = {
    accept: string
    className?: string
    maxSize: number
    onDrop: (acceptedFiles: File[], event: DropEvent) => void
}
export default function FileUploadButton({ accept, className, maxSize, onDrop }: Props) {
    const { enqueueSnackbar } = useSnackbar()

    const onDropRejected = (rejectedFiles: File[]) => {
        rejectedFiles.forEach((file) => {
            if (file.size > maxSize) {
                enqueueSnackbar(
                    interpolate(gettext('File must be smaller than %s MB'), [bytesToMB(maxSize)])
                )
            } else if (file.size === 0) {
                enqueueSnackbar(gettext('File cannot be empty'))
            } else if (!accept.includes(file.type)) {
                enqueueSnackbar(interpolate(gettext('%s files are not supported'), [file.type]), {
                    variant: 'error',
                })
            }
        })
    }

    return (
        <Dropzone
            maxSize={maxSize}
            accept={accept}
            onDropAccepted={onDrop}
            onDropRejected={onDropRejected}
        >
            {({ getRootProps, getInputProps, isDragActive }) => (
                <Box
                    sx={{
                        backgroundColor: (theme) =>
                            isDragActive ? theme.palette.grey[400] : undefined,
                    }}
                    {...getRootProps()}
                >
                    <input {...getInputProps()} />
                    <Button
                        color="inherit"
                        size="small"
                        sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}
                        className={className}
                        startIcon={<PublishIcon />}
                    >
                        {gettext('Upload')}
                    </Button>
                </Box>
            )}
        </Dropzone>
    )
}
